import { ComponentType, ReactElement } from 'react';
import { IconButton } from '@mui/material';
import * as MuiDatePickers from '@mui/x-date-pickers';
import { ArrowLeftIcon, ArrowRightIcon } from '@mui/x-date-pickers/icons';
import classNames from 'classnames';
import moment from 'moment';

import DownArrow from '../../assets/img/icon_expan_down_ratios.svg';
import LegacyClickableImage from '../../components/ClickableImage/ClickableImage';
import { DateRangeState } from '../../util/types';
import Button from '../Button/Button';
import { ButtonTypesEnum } from '../Button/Button.types';
import { CalendarViewEnum } from '../DesktopCalendar/DateRangePickerWrapper.types';

import styles from './MobileFromToDatePicker.module.scss';

const ClickableImage = LegacyClickableImage as unknown as ComponentType<any>;
const muiDatePickersExports = MuiDatePickers as Record<string, any>;
const PickersDay = (
  muiDatePickersExports['PickersDay']
  ?? muiDatePickersExports['PickerDay']
) as ComponentType<any>;

export type CalendarHeaderProps = {
  currentMonth: moment.Moment;
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  onMonthChange: (date: moment.Moment, direction: 'left' | 'right') => void;
  view: 'day' | 'month' | 'year';
  disablePast?: boolean;
  disableFuture?: boolean;
};

type MobileCalendarHeaderProps = {
  props: CalendarHeaderProps;
  onToggleView: (event, inputView: 'month' | 'year') => void;
};

export const MobileCalendarHeader = ({ props, onToggleView }: MobileCalendarHeaderProps): ReactElement => {

  const { currentMonth, minDate, maxDate, onMonthChange, view, disablePast, disableFuture } = props;

  const month = currentMonth.format('MMMM');

  const year = currentMonth.format('YYYY');

  const monthStart = currentMonth.clone().startOf('month');
  const nowMonth = moment().startOf('month');
  const minMonth = minDate ? moment(minDate).startOf('month') : undefined;
  const maxMonth = maxDate ? moment(maxDate).startOf('month') : undefined;

  const isPreviousDisabled = Boolean(
    (disablePast && monthStart.isSameOrBefore(nowMonth, 'month')) || (minMonth && monthStart.isSameOrBefore(minMonth, 'month')),
  );
  const isNextDisabled = Boolean(
    (disableFuture && monthStart.isSameOrAfter(nowMonth, 'month')) || (maxMonth && monthStart.isSameOrAfter(maxMonth, 'month')),
  );

  const handlePreviousMonth = (event) => {
    event.stopPropagation();
    if (!isPreviousDisabled) {

      onMonthChange(currentMonth.clone().subtract(1, 'month'), 'right');
    }
  };

  const handleNextMonth = (event) => {
    event.stopPropagation();
    if (!isNextDisabled) {
      onMonthChange(currentMonth.clone().add(1, 'month'), 'left');
    }
  };

  return (
    <div className={styles.customCalendarHeaderRoot}>
      <div className={styles.customCalendarHeader}>
        <Button className={styles.customCalendarHeaderButton} onClick={(e) => onToggleView(e, 'month')} variant={ButtonTypesEnum.Link}>
          <>
            {month}
            <ClickableImage
              src={DownArrow}
              className={classNames(styles.downArrow, view === CalendarViewEnum.Month && styles.arrowTransform)}
              alt="arrow icon"
            />
          </>
        </Button>
        <Button className={styles.customCalendarHeaderButton} onClick={(e) => onToggleView(e, 'year')} variant={ButtonTypesEnum.Link}>
          <>
            {year}
            <ClickableImage
              src={DownArrow}
              className={classNames(styles.downArrow, view === CalendarViewEnum.Year && styles.arrowTransform)}
              alt="arrow icon"
            />
          </>
        </Button>
      </div>
      {view === CalendarViewEnum.Day && (
        <div className={styles.customCalendarHeaderNavigation}>
          <IconButton
            className={styles.customCalendarNavButton}
            onClick={handlePreviousMonth}
            disabled={isPreviousDisabled}
          >
            <ArrowLeftIcon />
          </IconButton>
          <IconButton
            className={styles.customCalendarNavButton}
            onClick={handleNextMonth}
            disabled={isNextDisabled}
          >
            <ArrowRightIcon />
          </IconButton>
        </div>
      )}
    </div>
  );
};

const isRangeStartDate = (day: moment.Moment, tempRange: DateRangeState): boolean =>
  Boolean(tempRange.startDate && day.isSame(moment(tempRange.startDate), 'day'));

const isRangeEndDate = (day: moment.Moment, tempRange: DateRangeState): boolean =>
  Boolean(tempRange.endDate && day.isSame(moment(tempRange.endDate), 'day'));

const isDateInRange = (day: moment.Moment, tempRange: DateRangeState): boolean => {
  if (!tempRange.startDate || !tempRange.endDate) {
    return false;
  }

  const start = moment(tempRange.startDate).startOf('day');
  const end = moment(tempRange.endDate).startOf('day');
  const current = day.clone().startOf('day');

  if (start.isSame(end, 'day')) {
    return false;
  }

  return current.isAfter(start) && current.isBefore(end);
};

export const getMobileRenderDay = (tempRange: DateRangeState) =>
  (propsDay: any): ReactElement => {
    const { day } = propsDay;
    const isStartDate = isRangeStartDate(day, tempRange);
    const isEndDate = isRangeEndDate(day, tempRange);
    const isSingleDateSelection = isStartDate && isEndDate;
    const isInRange = isDateInRange(day, tempRange);

    return (
      <PickersDay
        {...propsDay}
        disableMargin
        selected={isStartDate || isEndDate || isInRange}
        sx={{
          ...(isInRange && {
            backgroundColor: '#3BB0FF26 !important',
            borderRadius: '0 !important',
            color: '#F2F4F7 !important',
          }),
          ...(isStartDate
            && !isSingleDateSelection && {
            borderRadius: '8px !important',
            backgroundColor: '#3BB0FF !important',
            color: '#0A0B13 !important',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#3BB0FF26',
              borderRadius: '8px 0 0 8px',
              pointerEvents: 'none',
            },
          }),
          ...(isEndDate
            && !isSingleDateSelection && {
            borderRadius: '8px !important',
            backgroundColor: '#3BB0FF !important',
            color: '#0A0B13 !important',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#3BB0FF26',
              borderRadius: '0 8px 8px 0',
              pointerEvents: 'none',
            },
          }),
          ...(isSingleDateSelection && {
            borderRadius: '8px !important',
            backgroundColor: '#3BB0FF !important',
            color: '#0A0B13 !important',
          }),
        }}
      />
    );
  };
